import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils.ts'
import { useDebouncedEffect } from '@/useDebouncedEffect.ts'
import api from '@/api.ts'
import useGlobalStore from '@/useGlobalStore.ts'
import { Chat, LibraryVocabulary } from '@/models.ts'
import { useState } from 'react'
import { BookOpenText, FilePlus2, Loader2, MessageSquareText, MessagesSquare } from 'lucide-react'
import WordInList from '@/components/word-in-list.tsx'
import { toast } from 'sonner'
import { motion, AnimatePresence } from 'motion/react'
import { Button } from '@/components/ui/button.tsx'
import useWordDetailsModalStore from '@/useWordDetailsModalStore.ts'

interface SearchResult {
	chats: []
	entries: []
	learnings: {
		vocabulary: LibraryVocabulary[]
	}
}

const PageSearch = ({
	className,
	searchQuery,
	showChat,
}: {
	className: string
	searchQuery: string
	showChat: (chatId: number) => void
}) => {
	const { t } = useTranslation()
	const { learningLanguageCode, loadLibraryOverview } = useGlobalStore()
	const [result, setResult] = useState<SearchResult>()
	const [isSearchLoading, setIsSearchLoading] = useState(false)
	// the query the current `result` belongs to, so the spinner also covers the debounce window
	const [resultQuery, setResultQuery] = useState('')
	const isSearching = searchQuery !== '' && (isSearchLoading || resultQuery !== searchQuery)
	const {
		showModal: showAddWordModal,
		setMode: setWordModalMode,
		setData,
		setOnSuccessfulFinalAction,
	} = useWordDetailsModalStore()

	const search = (query: string) => {
		if (searchQuery === '') {
			setResult(undefined)
			setResultQuery('')
			return
		}
		setIsSearchLoading(true)
		api(`/v1/search/global/${learningLanguageCode}?query=${query}`)
			.then((result: SearchResult) => {
				setResult(result)
			})
			.catch((x) => {
				toast(x.data?.message ?? t('Something went wrong'))
			})
			.finally(() => {
				setResultQuery(query)
				setIsSearchLoading(false)
			})
	}

	useDebouncedEffect(
		() => {
			search(searchQuery)
		},
		[searchQuery],
		1000,
	)

	const onAddWordToLibrary = async () => {
		// @ts-ignore
		setData({
			word: searchQuery,
			translation: '',
		})
		setWordModalMode('add/prefill')
		showAddWordModal()
		setOnSuccessfulFinalAction(async (_, libVoc) => {
			if (libVoc !== undefined) {
				result?.learnings.vocabulary.push(libVoc)
			}
			setTimeout(async () => {
				await loadLibraryOverview()
			}, 1500)
		})
	}

	return (
		<div className={cn(className, 'relative')}>
			<AnimatePresence>
				{isSearching && (
					<motion.div
						key="search-loading-spinner"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.15 }}
						className="absolute inset-0 z-20 flex items-center justify-center bg-background/60 backdrop-blur-[1px]">
						<Loader2 className="w-9 h-9 animate-spin" />
					</motion.div>
				)}
			</AnimatePresence>
			<AnimatePresence>
				{searchQuery !== '' && (
					<>
						<motion.div
							initial={{ y: -100, opacity: 0 }}
							animate={{ y: 0, opacity: 1 }}
							exit={{ y: -100, opacity: 0 }}
							transition={{ duration: 0.5 }}>
							<div className="flex items-center px-3 py-2 bg-gray-600 text-white">
								<BookOpenText className="w-4 h-4 me-2" />
								{t('Vocabulary')}
							</div>
							<div className="flex flex-col">
								{searchQuery !== '' &&
									!isSearching &&
									(result === null || result?.learnings.vocabulary.length === 0) && (
										<div className="m-2">
											{t('No results')}
											<br />
											<Button
												onClick={onAddWordToLibrary}
												className="mt-2"
												size="xs"
												variant="default">
												<FilePlus2 className="h-4 w-4 me-2" />
												{t('Add it to your library')}
											</Button>
										</div>
									)}
								{result?.learnings.vocabulary.map((libVoc: LibraryVocabulary, idx: number) => (
									<WordInList
										key={idx}
										index={idx}
										libraryVocabulary={libVoc}
										mode="summary"
										onWordDeleted={() => search(searchQuery)}
									/>
								))}
							</div>
						</motion.div>
						<motion.div
							initial={{ y: -100, opacity: 0 }}
							animate={{ y: 0, opacity: 1 }}
							exit={{ y: -100, opacity: 0 }}
							transition={{ duration: 0.5, delay: 0.2 }}>
							<div className="flex items-center px-3 py-2 bg-gray-600 text-white">
								<MessagesSquare className="w-4 h-4 me-2" />
								{t('Chats')}
							</div>
							<div className="flex flex-col">
								{searchQuery !== '' &&
									!isSearching &&
									(result === null || result?.chats.length === 0) && (
										<div className="m-2">{t('No results')}</div>
									)}
								{result?.chats.map((chat: Chat, idx: number) => (
									<div
										className="flex items-center active:bg-accent px-4 py-3"
										key={idx}
										onClick={() => showChat(chat.id)}>
										<div>
											<MessageSquareText className="w-4 h-4 me-3 mt-1" />
										</div>
										<div className="w-full select-none">{chat.title}</div>
									</div>
								))}
							</div>
						</motion.div>
					</>
				)}
			</AnimatePresence>
		</div>
	)
}

export default PageSearch
